import { useEffect, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { axiosInstance } from '../api/axios';
import { setCurrentRiskStandards } from '../components/RISK_ASSESSMENT/utils/riskStandard';
import { useSelector } from 'react-redux';
import { AppBar, Avatar, Toolbar, Typography, Box, useMediaQuery, useTheme, Divider, Menu, MenuItem, styled, IconButton, ToggleButton, ToggleButtonGroup, GlobalStyles } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from "../hooks/useAuth";
import { Icon } from '../components/icons';
import { useColorScheme } from '@mui/material/styles';

// 사이드바 컴포넌트
import SafeSidebar from '../components/SideBar/SafeSidebar';

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.primary.main
  }
}));

const MenuStyles = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  color: 'text.primary',
  textDecoration: 'none',
  '& svg': {
    mr: 2.5,
    fontSize: '1.5rem',
    color: 'text.secondary'
  }
};

const SAFE_APP_MODE_STORAGE_KEY = "safe-app-view-mode";
const SAFE_APP_MODES = {
  ADMIN: "admin",
  WORKER: "worker",
};

const SAFE_APP_FONT_STYLES = {
  ".safe-app-font-scope, .safe-app-font-scope *": {
    fontFamily:
      '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important',
    fontWeight: "400 !important",
    letterSpacing: "0 !important",
  },
  ".safe-app-font-scope": {
    color: "#111827",
  },
  ".safe-app-font-scope h1, .safe-app-font-scope h2, .safe-app-font-scope h3, .safe-app-font-scope h4, .safe-app-font-scope h5, .safe-app-font-scope h6, .safe-app-font-scope strong, .safe-app-font-scope b": {
    fontWeight: "800 !important",
  },
  ".safe-app-font-scope .MuiTypography-h1, .safe-app-font-scope .MuiTypography-h2, .safe-app-font-scope .MuiTypography-h3, .safe-app-font-scope .MuiTypography-h4, .safe-app-font-scope .MuiTypography-h5, .safe-app-font-scope .MuiTypography-h6": {
    fontWeight: "800 !important",
    lineHeight: "1.25 !important",
  },
  ".safe-app-font-scope .safe-home-page-title, .safe-app-font-scope .safe-home-section-title, .safe-app-font-scope .safe-home-kpi-title": {
    fontWeight: "800 !important",
  },
  ".safe-app-font-scope .MuiTypography-root.safe-page-header-title": {
    fontSize: "22px !important",
    fontWeight: "800 !important",
    lineHeight: "1.25 !important",
    color: "#111827 !important",
  },
  "@media (max-width: 899.95px)": {
    ".safe-app-font-scope .MuiTypography-root.safe-page-header-title": {
      fontSize: "20px !important",
    },
  },
  "@media (max-width: 599.95px)": {
    ".safe-app-font-scope .MuiTypography-root.safe-page-header-title": {
      fontSize: "18px !important",
    },
  },
  ".safe-app-font-scope .safe-home-kpi-value": {
    fontWeight: "800 !important",
  },
  ".safe-app-font-scope .MuiTypography-subtitle1, .safe-app-font-scope .MuiTypography-subtitle2": {
    fontWeight: "700 !important",
  },
  ".safe-app-font-scope .MuiTypography-body1": {
    fontSize: "14px !important",
    lineHeight: "1.5 !important",
  },
  ".safe-app-font-scope .MuiTypography-body2": {
    fontSize: "13px !important",
    lineHeight: "1.45 !important",
  },
  ".safe-app-font-scope .MuiTypography-caption": {
    fontSize: "12px !important",
    lineHeight: "1.35 !important",
  },
  ".safe-app-font-scope .MuiButton-root, .safe-app-font-scope .MuiToggleButton-root, .safe-app-font-scope .MuiChip-label, .safe-app-font-scope .MuiTableCell-head, .safe-app-font-scope .MuiDataGrid-columnHeaderTitle, .safe-app-font-scope .MuiTabs-root .MuiTab-root": {
    fontWeight: "700 !important",
  },
  ".safe-app-font-scope .MuiButton-root": {
    minHeight: "34px",
    borderRadius: "8px !important",
    padding: "7px 14px",
    fontSize: "13px !important",
    lineHeight: "1.35 !important",
    textTransform: "none !important",
    boxShadow: "none",
  },
  ".safe-app-font-scope .MuiButton-contained": {
    boxShadow: "0 4px 10px rgba(99, 102, 241, 0.18) !important",
  },
  ".safe-app-font-scope .MuiButton-contained:hover": {
    boxShadow: "0 6px 14px rgba(99, 102, 241, 0.24) !important",
  },
  ".safe-app-font-scope .MuiIconButton-root": {
    borderRadius: "8px !important",
  },
  ".safe-app-font-scope .MuiAlert-root": {
    borderRadius: "8px !important",
    alignItems: "center",
  },
  ".safe-app-font-scope .MuiAlert-message": {
    fontWeight: "400 !important",
    lineHeight: "1.5 !important",
  },
  ".safe-app-font-scope .MuiChip-root": {
    borderRadius: "6px !important",
    height: "24px",
    alignItems: "center",
  },
  ".safe-app-font-scope .MuiChip-label": {
    fontSize: "12px !important",
    lineHeight: "1.25 !important",
    paddingLeft: "8px !important",
    paddingRight: "8px !important",
  },
  ".safe-app-font-scope .MuiChip-icon": {
    fontSize: "16px !important",
    marginLeft: "6px !important",
    marginRight: "-2px !important",
  },
  ".safe-app-font-scope .MuiInputLabel-root, .safe-app-font-scope .MuiFormLabel-root, .safe-app-font-scope .MuiMenuItem-root, .safe-app-font-scope .MuiTableCell-body, .safe-app-font-scope .MuiListItemText-primary, .safe-app-font-scope .MuiListItemText-secondary": {
    fontWeight: "400 !important",
  },
  ".safe-app-font-scope .MuiInputBase-input, .safe-app-font-scope .MuiSelect-select, .safe-app-font-scope .MuiFormControlLabel-label": {
    fontSize: "14px !important",
    fontWeight: "400 !important",
  },
  ".safe-app-font-scope .MuiInputLabel-root, .safe-app-font-scope .MuiFormLabel-root": {
    fontSize: "12px !important",
    lineHeight: "1.3 !important",
  },
  ".safe-app-font-scope .MuiOutlinedInput-root": {
    borderRadius: "8px !important",
  },
  ".safe-app-font-scope .MuiPaper-root": {
    borderRadius: "8px",
  },
  ".safe-app-font-scope .MuiCard-root": {
    borderRadius: "8px !important",
  },
  ".safe-app-font-scope .MuiDialog-paper": {
    borderRadius: "10px !important",
    overflow: "hidden",
  },
  ".safe-app-font-scope .MuiTableCell-root": {
    fontSize: "13px !important",
    lineHeight: "1.45 !important",
  },
  ".safe-app-font-scope .MuiTableCell-head": {
    color: "#374151 !important",
    backgroundColor: "#f8fafc",
    fontSize: "13px !important",
    lineHeight: "1.35 !important",
    textAlign: "center",
  },
  ".safe-app-font-scope .MuiTableContainer-root": {
    backgroundColor: "#fff",
  },
  ".safe-app-font-scope .MuiTablePagination-root": {
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#fff",
  },
  ".safe-app-font-scope .MuiTableRow-root:last-of-type .MuiTableCell-root": {
    borderBottomColor: "#e5e7eb",
  },
  ".safe-app-font-scope .MuiTableCell-body": {
    color: "#1f2937",
  },
  ".safe-app-font-scope .MuiDataGrid-columnHeaderTitle": {
    fontSize: "13px !important",
  },
  ".safe-app-font-scope .MuiDataGrid-cell": {
    fontSize: "13px !important",
    fontWeight: "400 !important",
  },
  ".safe-app-font-scope .MuiDialogTitle-root": {
    fontSize: "18px !important",
    fontWeight: "800 !important",
    lineHeight: "1.35 !important",
    padding: "18px 24px 14px !important",
    borderBottom: "1px solid #e5e7eb",
  },
  ".safe-app-font-scope .MuiDialogContent-root": {
    fontWeight: "400 !important",
  },
  ".safe-app-font-scope .MuiDialogContent-dividers": {
    borderTop: "0 !important",
    borderBottom: "1px solid #e5e7eb !important",
  },
  ".safe-app-font-scope .MuiDialogActions-root": {
    padding: "14px 24px !important",
    gap: "8px",
    borderTop: "1px solid #e5e7eb",
    justifyContent: "flex-end",
  },
  ".safe-app-font-scope .MuiCard-root[role='button'], .safe-app-font-scope .MuiPaper-root[role='button'], .safe-app-font-scope [data-clickable='true']": {
    cursor: "pointer",
    transition:
      "border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease, background-color 0.16s ease",
  },
  ".safe-app-font-scope .MuiCard-root[role='button']:hover, .safe-app-font-scope .MuiPaper-root[role='button']:hover, .safe-app-font-scope [data-clickable='true']:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
  },
  ".safe-app-font-scope .safe-sidebar-desktop, .safe-app-font-scope .safe-sidebar-mobile, .safe-app-font-scope .safe-sidebar-desktop *, .safe-app-font-scope .safe-sidebar-mobile *, .safe-app-font-scope .ps-menu-label, .safe-app-font-scope .ps-menu-button": {
    fontWeight: "600 !important",
  },
  ".safe-app-font-scope .ps-active, .safe-app-font-scope .ps-active *, .safe-app-font-scope .ps-menu-button.ps-active, .safe-app-font-scope .ps-menu-button.ps-active *": {
    fontWeight: "700 !important",
  },
  ".MuiDialog-root .MuiTypography-root, .MuiDialog-root .MuiInputLabel-root, .MuiDialog-root .MuiFormLabel-root, .MuiPopover-root .MuiTypography-root, .MuiMenu-root .MuiTypography-root, .MuiModal-root .MuiTypography-root": {
    fontWeight: "400 !important",
  },
  ".MuiDialog-root .MuiDialogTitle-root, .MuiDialog-root .MuiButton-root, .MuiDialog-root .MuiChip-label, .MuiPopover-root .MuiMenuItem-root, .MuiMenu-root .MuiMenuItem-root": {
    fontWeight: "700 !important",
  },
  ".MuiDialog-root .MuiButton-root": {
    borderRadius: "8px !important",
    textTransform: "none !important",
  },
  ".Toastify__toast": {
    borderRadius: "10px !important",
    fontFamily:
      '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important',
    fontSize: "13px !important",
    lineHeight: "1.45 !important",
  },
  ".Toastify__toast-body, .Toastify__toast-body *": {
    fontWeight: "400 !important",
  },
  ".Toastify__toast-body strong, .Toastify__toast-body b": {
    fontWeight: "800 !important",
  },
};

const SafeAppLayout = () => {
  const { group_code, project_code } = useParams();
  const [, refreshCriteria] = useState(0);
  useEffect(() => {
    let active = true;
    setCurrentRiskStandards([]);
    const load = () => {
      if (!group_code || !project_code) return;
      axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/risk-criteria`).then(({ data }) => {
        if (active) { setCurrentRiskStandards(data.standards); refreshCriteria(n => n + 1); }
      }).catch(() => {});
    };
    load();
    window.addEventListener('safe-criteria-updated', load);
    window.addEventListener('focus', load);
    return () => { active = false; setCurrentRiskStandards([]); window.removeEventListener('safe-criteria-updated', load); window.removeEventListener('focus', load); };
  }, [group_code, project_code]);
  const user = useSelector((state) => state.auth.user);
  const theme = useTheme();
  const auth = useAuth();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();

  // 아바타 드롭다운
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [safeAppMode, setSafeAppMode] = useState(() => {
    const savedMode = localStorage.getItem(SAFE_APP_MODE_STORAGE_KEY);
    return savedMode === SAFE_APP_MODES.WORKER
      ? SAFE_APP_MODES.WORKER
      : SAFE_APP_MODES.ADMIN;
  });
  const isWorkerMode = safeAppMode === SAFE_APP_MODES.WORKER;
  const isMobileSidebar = isWorkerMode ? !isLgUp : !isMdUp;

  useEffect(() => {
    if (!isMobileSidebar) {
      setMobileSidebarOpen(false);
    }
  }, [isMobileSidebar]);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.logout();
  };

  // MUI 컬러 스키마 훅 사용
  const { mode, setMode } = useColorScheme();

  // 테마 모드 전환 핸들러
  const handleThemeChange = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('insightwin-mui-mode', newMode);
  };

  const handleSafeAppModeChange = (_, nextMode) => {
    if (!nextMode) return;
    setSafeAppMode(nextMode);
    localStorage.setItem(SAFE_APP_MODE_STORAGE_KEY, nextMode);
  };

  return (
    <Box className="safe-app-font-scope" sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <GlobalStyles styles={SAFE_APP_FONT_STYLES} />
      {/* 상단 전체를 차지하는 AppBar */}
      <AppBar
        color="default"
        elevation={2}
        position="sticky"
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          boxShadow: "none",
          alignItems: "center",
          color: "text.primary",
          justifyContent: "center",
          transition: "border-bottom 0.2s ease-in-out",
          backgroundColor: "rgba(255, 255, 255, 0.88)",
        })}
      >
        <Toolbar
          disableGutters
          sx={{
            width: "100%",
            minHeight: { xs: "64px", md: "64px" },
            paddingInline: { xs: "12px", md: isMobileSidebar ? "16px" : "30px" },
          }}
        >
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%"
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              {isMobileSidebar && (
                <IconButton
                  onClick={() => setMobileSidebarOpen((open) => !open)}
                  sx={{ mr: 1 }}
                  aria-label="메뉴 열기"
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography 
                variant={isMobileSidebar ? "h6" : "h4"}
                sx={{ 
                  fontWeight: 800, 
                  lineHeight: "24px", 
                  opacity: 0.8,
                  marginRight: { xs: 1, md: 2 },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: 138, sm: "none" },
                }}>
                안전관리 시스템
              </Typography>
            </Box>

            <Box
              sx={{
                display: { xs: mobileSidebarOpen ? "none" : "flex", md: "flex" },
                alignItems: "center",
                gap: { xs: 0.75, sm: 1.25 },
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={safeAppMode}
                onChange={handleSafeAppModeChange}
                aria-label="안전관리 시스템 모드"
                sx={{
                  p: 0.35,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  bgcolor: "background.paper",
                  flexShrink: 0,
                  "& .MuiToggleButton-root": {
                    minWidth: { xs: 44, sm: 64 },
                    px: { xs: 0.8, sm: 1.2 },
                    py: 0.45,
                    border: 0,
                    borderRadius: "8px !important",
                    color: "text.secondary",
                    fontSize: { xs: 12, sm: 13 },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    "&.Mui-selected": {
                      color: "primary.main",
                      bgcolor: "primary.lightOpacity",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "primary.lightOpacity",
                    },
                  },
                }}
              >
                <ToggleButton value={SAFE_APP_MODES.ADMIN}>
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    관리자
                  </Box>
                  <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                    관
                  </Box>
                </ToggleButton>
                <ToggleButton value={SAFE_APP_MODES.WORKER}>
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    근로자
                  </Box>
                  <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                    근
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: '6px' }}>
                <IconButton onClick={handleThemeChange}>
                  <Icon 
                    name={mode === 'light' ? "sunOutlineIcon" : "moonOutlineIcon"} 
                    style={{ 
                      width: '26px', 
                      height: '26px', 
                      '--stroke-width': '3px' 
                    }} 
                  />
                </IconButton>
                <IconButton>
                  <Icon name="bellOutlineIcon" style={{ width: '26px', height: '26px', '--stroke-width': '3px' }} />
                </IconButton>
                <IconButton onClick={handleDropdownOpen}>
                  <Icon name="userIcon" style={{ width: '26px', height: '26px', '--stroke-width': '3px' }} />
                </IconButton>
              </Box>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleDropdownClose}
              sx={{
                "& .MuiMenu-paper": {
                  width: 230,
                  mt: "12px",
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", px: 5, py: 1 }}>
                <Avatar sx={{ width: 38, height: 38, mr: 4 }}>
                  <Icon name="userIcon" />
                </Avatar>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6">{user.user_name}</Typography>
                  <Typography variant="body2" sx={{ color: "text.disabled" }}>
                    {user.group_name}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              <MenuItemStyled onClick={handleDropdownClose}>
                <Box sx={MenuStyles}>
                  <Icon name="userEditIcon" style={{ width: "22px", height: "22px" }} />
                  프로필 설정
                </Box>
              </MenuItemStyled>
              <MenuItemStyled onClick={handleLogout}>
                <Box sx={MenuStyles}>
                  <Icon name="logoutIcon" style={{ width: "22px", height: "22px" }} />
                  로그아웃
                </Box>
              </MenuItemStyled>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 사이드바 + 메인 컨텐츠 영역 */}
      <Box sx={{ flex: 1, display: "flex", minWidth: 0, overflow: "hidden" }}>
        {/* 좌측 사이드바 */}
        <SafeSidebar
          isMobile={isMobileSidebar}
          toggled={mobileSidebarOpen}
          onToggle={setMobileSidebarOpen}
        />

        {/* 컨텐츠 영역 */}
        <Box sx={{ 
          flexGrow: 1,
          minWidth: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          bgcolor: 'background.default'
        }}>
          <Outlet context={{ safeAppMode }} />
        </Box>
      </Box>
    </Box>
  );
};

export default SafeAppLayout;
